import React, { useEffect, useMemo } from 'react';
import { App, DatePicker, Form, Input, Modal, Select, Space, Typography, theme } from 'antd';
import { BellOutlined, TeamOutlined, UserOutlined } from '@ant-design/icons';
import dayjs, { type Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { useClientEmployeesQuery } from '@/api/auth/queries';
import { useCreateReminderMutation } from '@/api/mcp-reminder/mutations';
import { useReminderPreferencesQuery } from '@/api/mcp-reminder/queries';
import type { ReminderAudience } from '@/api/mcp-reminder/interfaces';
import type { CreateReminderRequest, Reminder } from '@/api/mcp-reminder/interfaces';
import { useAdminClients, useEffectiveClientId } from '@/contexts/AdminClientsContext';
import { canUseAdminTenantScope } from '@/utils/authz';
import { canSetReminderLevel } from '@/utils/reminders';
import { ModalTitle } from '@/components/ui/ModalTitle';
import { FORM_MODAL_STYLES, formModalWidth } from '@/components/ui/formModalStyles';
import { FormModalFooter } from '@/components/ui/FormModalFooter';

const { Text } = Typography;

/** Preset notify-before options (minutes); max matches API preference limit (24h). */
const NOTIFY_LEAD_MINUTE_OPTIONS = [0, 5, 10, 15, 30, 60, 120, 1440] as const;

/** Fallback notify lead time used when user preference is missing. */
const DEFAULT_NOTIFY_LEAD_MINUTES = 15;

/** Builds a localized label for a notify lead option in minutes. */
function notifyLeadOptionLabel(minutes: number, t: (key: string, opts?: { count: number }) => string): string {
  if (minutes === 0) return t('reminders.modal.notifyLeadWhenDue');
  if (minutes % 60 === 0 && minutes >= 60) {
    return t('reminders.modal.notifyLeadHoursBefore', { count: minutes / 60 });
  }
  return t('reminders.modal.notifyLeadMinutesBefore', { count: minutes });
}

/** Shape of reminder create form values managed by Ant Design Form. */
type FormValues = {
  title: string;
  dueAt: Dayjs;
  notifyLeadMinutes: number;
  audience?: ReminderAudience;
  targetUserId?: string;
  description?: string;
};

export interface ReminderFormInitialValues {
  title?: string;
  dueAt?: Dayjs | Date | string;
  notifyLeadMinutes?: number;
  audience?: ReminderAudience;
  targetUserId?: string;
  description?: string;
}

/** Props for opening and closing the reminder creation modal. */
export interface ReminderFormModalProps {
  open: boolean;
  onClose: () => void;
  initialValues?: ReminderFormInitialValues;
  context?: CreateReminderRequest['context'];
  onSuccess?: (reminder: Reminder) => void | Promise<void>;
}

/** Reminder creation modal with scheduling, audience, and optional note fields. */
export default function ReminderFormModal({
  open,
  onClose,
  initialValues,
  context,
  onSuccess
}: ReminderFormModalProps): React.ReactElement {
  const { t } = useTranslation('manage');
  const { token } = theme.useToken();
  const { message } = App.useApp();
  const [form] = Form.useForm<FormValues>();
  const { payload } = useAuth();
  const { selectedClientId } = useAdminClients();
  const effectiveClientId = useEffectiveClientId();
  const createReminder = useCreateReminderMutation();
  const { data: preference } = useReminderPreferencesQuery(open);
  const showLevel = canSetReminderLevel(payload?.role, payload?.tenantRoles);
  const audience = Form.useWatch('audience', form);
  const clientId = canUseAdminTenantScope(payload?.role) ? selectedClientId : effectiveClientId;
  const showAssignee = showLevel && audience === 'USER';
  const { data: employeesData, isLoading: employeesLoading } = useClientEmployeesQuery(
    clientId ?? '',
    open && showAssignee && !!clientId
  );

  /** Options for assignee select, including current user fallback when needed. */
  const assigneeOptions = useMemo(() => {
    const users = employeesData?.users ?? [];
    const options = users.map((u) => ({ value: u.id, label: u.name || u.email }));
    if (payload?.userId && !options.some((o) => o.value === payload.userId)) {
      options.unshift({ value: payload.userId, label: payload.email || payload.userId });
    }
    return options;
  }, [employeesData?.users, payload?.userId, payload?.email]);

  /** Reminder preference-driven default notify lead time. */
  const defaultNotifyLeadMinutes = preference?.defaultNotifyLeadMinutes ?? DEFAULT_NOTIFY_LEAD_MINUTES;

  /** User-facing notify lead options sorted by minute value. */
  const notifyLeadOptions = useMemo(() => {
    const values = new Set<number>(NOTIFY_LEAD_MINUTE_OPTIONS);
    if (Number.isFinite(defaultNotifyLeadMinutes)) {
      values.add(defaultNotifyLeadMinutes);
    }
    return [...values]
      .sort((a, b) => a - b)
      .map((minutes) => ({
        value: minutes,
        label: notifyLeadOptionLabel(minutes, t)
      }));
  }, [t, defaultNotifyLeadMinutes]);

  const resolvedInitialDueAt = useMemo(() => {
    if (!initialValues?.dueAt) return undefined;
    const parsed = dayjs(initialValues.dueAt);
    return parsed.isValid() ? parsed.startOf('minute') : undefined;
  }, [initialValues?.dueAt]);

  useEffect(() => {
    if (!open) return;
    form.setFieldsValue({
      title: initialValues?.title,
      description: initialValues?.description,
      dueAt: resolvedInitialDueAt ?? dayjs().add(1, 'hour').startOf('minute'),
      notifyLeadMinutes: initialValues?.notifyLeadMinutes ?? defaultNotifyLeadMinutes,
      audience: initialValues?.audience ?? 'USER',
      targetUserId: initialValues?.targetUserId ?? payload?.userId
    });
  }, [open, form, payload?.userId, defaultNotifyLeadMinutes, initialValues, resolvedInitialDueAt]);

  /** Resets form and closes the modal without creating a reminder. */
  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  /** Validates and submits reminder creation payload. */
  const handleSubmit = async (values: FormValues) => {
    if (!payload?.userId) return;
    const resolvedAudience = showLevel ? values.audience || 'USER' : 'USER';
    const targetUserId =
      resolvedAudience === 'USER'
        ? showLevel
          ? values.targetUserId || payload.userId
          : payload.userId
        : undefined;

    try {
      const createdReminder = await createReminder.mutateAsync({
        title: values.title.trim(),
        description: values.description?.trim() || undefined,
        dueAt: values.dueAt.startOf('minute').toISOString(),
        audience: resolvedAudience,
        targetUserId,
        notifyLeadMinutes: values.notifyLeadMinutes,
        automatic: false,
        context
      });
      message.success(t('reminders.modal.success'));
      form.resetFields();
      await onSuccess?.(createdReminder);
      onClose();
    } catch {
      message.error(t('reminders.modal.error'));
    }
  };

  return (
    <Modal
      title={
        <ModalTitle
          icon={<BellOutlined />}
          title={t('reminders.modal.title')}
          subtitle={t('reminders.modal.description')}
        />
      }
      open={open}
      onCancel={handleCancel}
      footer={null}
      width={formModalWidth(520)}
      destroyOnHidden
      styles={{ body: { ...FORM_MODAL_STYLES.body, paddingTop: token.paddingMD } }}>
      <Form
        form={form}
        layout="vertical"
        requiredMark="optional"
        onFinish={(values) => void handleSubmit(values)}
        initialValues={{
          audience: 'USER',
          targetUserId: payload?.userId,
          dueAt: dayjs().add(1, 'hour').startOf('minute'),
          notifyLeadMinutes: defaultNotifyLeadMinutes
        }}>
        <Form.Item
          name="title"
          label={t('reminders.modal.titleLabel')}
          rules={[{ required: true, message: t('reminders.modal.titleRequired') }]}>
          <Input placeholder={t('reminders.modal.titlePlaceholder')} allowClear />
        </Form.Item>

        <Form.Item
          name="dueAt"
          label={t('reminders.modal.dueLabel')}
          rules={[
            { required: true, message: t('reminders.modal.dueRequired') },
            {
              validator: (_, value: Dayjs | undefined) => {
                if (!value || value.isAfter(dayjs())) return Promise.resolve();
                return Promise.reject(new Error(t('reminders.modal.dueFuture')));
              }
            }
          ]}>
          <DatePicker
            showTime={{ format: 'HH:mm' }}
            format="YYYY-MM-DD HH:mm"
            style={{ width: '100%' }}
            disabledDate={(d) => !!d && d < dayjs().startOf('day')}
          />
        </Form.Item>

        <Form.Item
          name="notifyLeadMinutes"
          label={t('reminders.modal.notifyLeadLabel')}
          extra={
            defaultNotifyLeadMinutes !== undefined ? (
              <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                {t('reminders.modal.notifyLeadDefaultHint', { minutes: defaultNotifyLeadMinutes })}
              </Text>
            ) : null
          }
          rules={[{ required: true, message: t('reminders.modal.notifyLeadRequired') }]}>
          <Select options={notifyLeadOptions} />
        </Form.Item>

        {showLevel ? (
          <Form.Item name="audience" label={t('reminders.modal.levelLabel')} rules={[{ required: true }]}>
            <Select
              options={[
                {
                  value: 'USER',
                  label: (
                    <Space>
                      <UserOutlined />
                      <span>{t('reminders.modal.levelUser')}</span>
                      <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                        — {t('reminders.modal.levelUserHint')}
                      </Text>
                    </Space>
                  )
                },
                {
                  value: 'TENANT',
                  label: (
                    <Space>
                      <TeamOutlined />
                      <span>{t('reminders.modal.levelTenant')}</span>
                      <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                        — {t('reminders.modal.levelTenantHint')}
                      </Text>
                    </Space>
                  )
                }
              ]}
            />
          </Form.Item>
        ) : null}

        {showAssignee ? (
          <Form.Item
            name="targetUserId"
            label={t('reminders.modal.assigneeLabel')}
            rules={[{ required: true, message: t('reminders.modal.assigneeRequired') }]}>
            <Select
              showSearch
              optionFilterProp="label"
              loading={employeesLoading}
              options={assigneeOptions}
              placeholder={t('reminders.modal.assigneePlaceholder')}
            />
          </Form.Item>
        ) : null}

        <Form.Item name="description" label={t('reminders.modal.noteLabel')}>
          <Input.TextArea
            rows={3}
            placeholder={t('reminders.modal.notePlaceholder')}
            showCount
            maxLength={500}
          />
        </Form.Item>

        <FormModalFooter
          onCancel={handleCancel}
          submitLabel={t('reminders.modal.save')}
          loading={createReminder.isPending}
        />
      </Form>
    </Modal>
  );
}
